import { navCategories, latestNews } from "@/mocks/homeNews";

interface TodayFeedProps {
  cat: string;
  onCat: (cat: string) => void;
  query: string;
  goRead: () => void;
}

export default function TodayFeed({ cat, onCat, query, goRead }: TodayFeedProps) {
  const kw = query.trim();
  const filtered = latestNews.filter((it) => {
    const byCat = cat === "全部" || it.cat === cat;
    const byKw = !kw || it.title.includes(kw) || it.summary.includes(kw);
    return byCat && byKw;
  });

  const shown = filtered.slice(0, 6);
  const rest = filtered.slice(6);

  return (
    <section id="today" className="scroll-mt-24">
      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
        {/* section head */}
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-background-50 dark:text-foreground-950">
                71瓜田速报
              </span>
              {kw && (
                <span className="text-sm text-foreground-500">
                  搜到 <b className="text-primary-600">{filtered.length}</b> 条与「{kw}」相关
                </span>
              )}
            </div>
            <h2 className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              今日速报与快报
            </h2>
            <p className="mt-2 text-sm text-foreground-500">
              最新动态、热门瓜事与网友讨论都放一起，不用到处翻，更新后拿起就能接着看。
            </p>
          </div>

          {/* tabs */}
          <div className="no-scrollbar flex gap-1 overflow-x-auto rounded-full border border-background-300/70 bg-background-100 p-1">
            {["全部", ...navCategories].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => onCat(c)}
                className={
                  "whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition " +
                  (cat === c
                    ? "bg-primary-500 font-semibold text-background-50 shadow-sm dark:text-foreground-950"
                    : "text-foreground-600 hover:bg-background-50")
                }
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {shown.length === 0 && (
          <div className="mt-10 flex flex-col items-center rounded-2xl border border-dashed border-background-300 bg-background-100 py-16 text-center">
            <i className="ri-search-eye-line text-4xl text-foreground-300" />
            <p className="mt-3 text-foreground-600">这一瓜田暂时还没动静，换个词或分类试试吧。</p>
            <button
              type="button"
              onClick={() => onCat("全部")}
              className="mt-4 cursor-pointer rounded-full bg-primary-500 px-5 py-2 text-sm text-background-50 dark:text-foreground-950"
            >
              看全部
            </button>
          </div>
        )}

        {/* big cards */}
        {shown.length > 0 && (
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((it) => (
              <article
                key={it.id}
                onClick={goRead}
                className="group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition hover:-translate-y-1 hover:border-primary-300/70"
              >
                <div className="relative h-44 w-full overflow-hidden">
                  <img
                    src={it.img}
                    alt={`${it.cat}：${it.title}`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-background-950/70 px-2.5 py-1 text-xs text-background-50 backdrop-blur">
                    {it.cat}
                  </span>
                  {it.hot && (
                    <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-accent-500 px-2 py-1 text-xs font-bold text-background-50 dark:text-foreground-950">
                      <i className="ri-fire-fill" /> 热
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-4 md:p-5">
                  <h3 className="line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-950 transition group-hover:text-primary-700">
                    {it.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-500">{it.summary}</p>
                  <div className="mt-auto flex items-center justify-between pt-3 text-xs text-foreground-400">
                    <span className="flex items-center gap-1"><i className="ri-time-line" />{it.time}</span>
                    <span className="flex items-center gap-1"><i className="ri-eye-line" />{it.views}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {rest.length > 0 && (
          <>
            <h3 className="mt-10 font-heading text-lg font-bold text-foreground-950">更多同栏瓜事</h3>
            <div className="mt-4 divide-y divide-background-200/70 overflow-hidden rounded-2xl border border-background-200 bg-background-50">
              {rest.map((it) => (
                <a key={it.id} href="#today" className="group flex items-center gap-3 px-4 py-3.5 transition hover:bg-secondary-100/60 md:px-5">
                  <span className="flex h-2 w-2 shrink-0 rounded-full bg-primary-500" />
                  <span className="line-clamp-1 flex-1 text-sm text-foreground-800 transition group-hover:text-primary-700">{it.title}</span>
                  <span className="shrink-0 whitespace-nowrap text-xs text-foreground-400"><i className="ri-eye-line mr-1" />{it.views}</span>
                </a>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}